import * as React from 'react';
import { useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import cx from 'classnames';
import Loader from 'components/Loader/Loader';

const tableStyles = require('../TravelerDetails/styles.pcss');
const styles = require('./recordStyles.pcss');

export type Column = {
  label: string;
  render: (record: any, index: number) => React.ReactNode;
};

export type TabConfig = {
  label: string;
  filter: (record: any) => boolean;
};

export type BulkAction = {
  label: string;
  onClick: (selectedIds: string[]) => void;
  color?: 'primary' | 'error';
  disabled?: (selectedIds: string[]) => boolean;
};

type Props = {
  columns: Column[];
  records: any[];
  tabs: TabConfig[];
  isLoading: boolean;
  onClickRow?: (record: any) => void;
  keyField?: string;
  selectable?: boolean;
  bulkActions?: BulkAction[];
};

const RecordsTable = (props: Props) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const {
    columns,
    records,
    tabs,
    isLoading,
    onClickRow,
    keyField,
    selectable,
    bulkActions,
  } = props;
  const activeTab = tabs[activeTabIndex];
  const filtered = activeTab ? records.filter(activeTab.filter) : records;
  const clickable = !!onClickRow;
  const totalColumns = columns.length + (selectable ? 1 : 0);

  const getRecordId = (record: any): string =>
    keyField ? String(record[keyField] ?? '') : '';

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleToggleAll = () => {
    const filteredIds = filtered.map(getRecordId);
    const allSelected = filteredIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedIds((prev) => [
        ...prev,
        ...filteredIds.filter((id) => !prev.includes(id)),
      ]);
    }
  };

  const handleTabChange = (_e: any, idx: number) => {
    setActiveTabIndex(idx);
    setSelectedIds([]);
  };

  const filteredIds = filtered.map(getRecordId);
  const numSelected = selectedIds.filter((id) => filteredIds.includes(id)).length;
  const allSelected = filteredIds.length > 0 && numSelected === filteredIds.length;
  const indeterminate = numSelected > 0 && numSelected < filteredIds.length;

  return (
    <div className="pageWrapper">
      <Loader visible={isLoading} />

      <Tabs
        className={tableStyles.tabbs}
        value={activeTabIndex}
        indicatorColor="primary"
        textColor="primary"
        onChange={handleTabChange}
      >
        {tabs.map((tab, i) => (
          <Tab key={i} className={tableStyles.tabb} label={tab.label} />
        ))}
      </Tabs>

      {selectable && bulkActions && numSelected > 0 && (
        <div className={styles.bulkActionBar}>
          <span className={styles.bulkSelectionCount}>
            {numSelected} selected
          </span>
          {bulkActions.map((action, i) => (
            <button
              key={i}
              className={cx(styles.bulkActionButton, {
                [styles.bulkActionButtonError]: action.color === 'error',
              })}
              disabled={action.disabled ? action.disabled(selectedIds) : false}
              onClick={() => {
                action.onClick(selectedIds);
                setSelectedIds([]);
              }}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      <Table className={tableStyles.expenseReports}>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableCell className={tableStyles.tableHead} padding="checkbox">
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allSelected}
                  onChange={handleToggleAll}
                  inputProps={{ 'aria-label': 'select all' }}
                />
              </TableCell>
            )}
            {columns.map((col, i) => (
              <TableCell key={i} className={tableStyles.tableHead}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={totalColumns}
                className={styles.emptyMessage}
              >
                No records found
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((record, i) => {
              const id = getRecordId(record);
              const key = id || i;
              const isSelected = selectedIds.includes(id);
              return (
                <TableRow
                  className={cx(tableStyles.tableRow, {
                    [styles.clickableRow]: clickable,
                  })}
                  key={key}
                  selected={isSelected}
                  onClick={clickable ? () => onClickRow!(record) : undefined}
                >
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleToggle(id)}
                        inputProps={{ 'aria-label': `select ${id}` }}
                      />
                    </TableCell>
                  )}
                  {columns.map((col, ci) => (
                    <TableCell
                      key={ci}
                      className={tableStyles.tableRowCell}
                      scope={ci === 0 ? 'row' : undefined}
                    >
                      {col.render(record, i)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default RecordsTable;
